import { headingNotes, LIMITS, setHeadingNotes, type HeadingScope } from '@blueprint/domain'
import { useEffect, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, TextArea } from '../../components/controls.js'
import { useBoardEditable, useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './HeadingNotes.module.css'

const SCOPE_LABELS: Record<HeadingScope, string> = { group: 'group', column: 'column', swimlane: 'swimlane', row: 'row' }

/**
 * The notes icon on a heading. Filled when the heading has notes; otherwise it appears
 * only while the heading is hovered or focused, so empty headings stay clean.
 */
export function HeadingNotesButton({ scope, id, title }: { scope: HeadingScope; id: string; title: string }) {
  const notes = useBoardStore((state) => (state.entities ? headingNotes(state.entities, scope, id) : ''))
  const has = notes.trim().length > 0
  return (
    <button
      type="button"
      className={`${styles.button} ${has ? styles.filled : styles.empty}`}
      aria-label={`${has ? 'Notes' : 'Add notes'} for ${SCOPE_LABELS[scope]} ${title}`}
      title={has ? notes.slice(0, 200) : 'Notes'}
      data-testid="heading-notes"
      onClick={() => useUiStore.getState().setHeadingNotes({ scope, id })}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M3.5 1.75h6.25L13 5v9.25H3.5z"
          fill={has ? 'currentColor' : 'none'}
          fillOpacity={has ? 0.18 : 0}
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M5.75 8h5M5.75 10.75h5M5.75 5.25h2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    </button>
  )
}

/** View and edit a heading's notes. Read-only for viewers. */
export function HeadingNotesDialog() {
  const target = useUiStore((state) => state.headingNotes)
  const close = useUiStore((state) => state.setHeadingNotes)
  const editable = useBoardEditable()
  const entity = useBoardStore((state) => {
    if (!target || !state.entities) return null
    const { scope, id } = target
    const s = state.entities
    return scope === 'group' ? s.groups[id] : scope === 'column' ? s.columns[id] : scope === 'swimlane' ? s.swimlanes[id] : s.rows[id]
  })
  const [draft, setDraft] = useState('')
  const [mode, setMode] = useState<'view' | 'edit'>('view')

  useEffect(() => {
    if (!target) return
    const notes = entity?.notes ?? ''
    setDraft(notes)
    // Straight to editing when there is nothing to read yet.
    setMode(notes.trim() ? 'view' : 'edit')
    // Only when a different heading is opened, not on every board change.
  }, [target?.scope, target?.id])

  if (!target || !entity) return null
  const notes = entity.notes ?? ''
  const save = () => {
    useBoardStore.getState().run((state) => setHeadingNotes(state, target.scope, target.id, draft))
    close(null)
  }
  const editing = editable && mode === 'edit'

  return (
    <Dialog
      open
      title={`Notes — ${entity.title}`}
      onClose={() => close(null)}
      onSubmit={editing ? save : undefined}
      footer={
        editing ? (
          <>
            <Button onClick={() => (notes.trim() ? setMode('view') : close(null))}>Cancel</Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </>
        ) : (
          <>
            {editable ? <Button onClick={() => setMode('edit')}>Edit</Button> : null}
            <Button variant="primary" onClick={() => close(null)}>
              Close
            </Button>
          </>
        )
      }
    >
      <p className={styles.scope}>{SCOPE_LABELS[target.scope]}</p>
      {editing ? (
        <TextArea
          aria-label="Notes"
          data-autofocus
          rows={12}
          value={draft}
          maxLength={LIMITS.description}
          placeholder="Notes, description, decisions…"
          onChange={(event) => setDraft(event.target.value)}
        />
      ) : (
        <div className={styles.text} data-testid="heading-notes-text">
          {notes.trim() ? notes : <span className={styles.none}>No notes.</span>}
        </div>
      )}
    </Dialog>
  )
}
